import type {
  InventionElementResponse,
  ServedQuestion,
} from "@patent-studio/api-types";
import { cn } from "@/lib/utils";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import { isAwaitingAttention, tagOf } from "./element-tags";

/** One row: what kind of difference it is, and the words for it. */
interface Row {
  key: string;
  kind: "agree" | "framing" | "added" | "missing";
  lead: string;
  body: string;
}

/**
 * The mark before each row. A framing and an addition take their tag's own
 * colours from the one tag table, so a recolour there reaches here.
 */
const MARK: Record<Row["kind"], { glyph: string; className: string }> = {
  agree: { glyph: "✓", className: "bg-success-surface text-success-strong" },
  framing: { glyph: "◆", className: tagOf("ai_framing").chipClass },
  added: { glyph: "▲", className: tagOf("unverified").chipClass },
  missing: { glyph: "?", className: "bg-muted text-muted-foreground" },
};

/** A question still waiting: open, or skipped to answer later. */
const UNANSWERED = new Set<ServedQuestion["status"]>(["open", "parked"]);

/**
 * The rows, from the aggregate and the question store: the pass's one agreed sentence when the attorney
 * gave an account, then every framing still to confirm, every element the
 * attorney added without a source, and every gap the store holds unanswered.
 *
 * A framing once confirmed, an added element once settled, and a gap once
 * answered or closed no longer differ, so they drop out. A gap skipped at the
 * card is still missing, so it stays.
 */
export function differenceRows(
  agreementNote: string | null,
  elements: readonly InventionElementResponse[],
  questions: readonly ServedQuestion[],
): Row[] {
  const rows: Row[] = [];
  if (agreementNote !== null && agreementNote.trim() !== "") {
    rows.push({
      key: "agree",
      kind: "agree",
      lead: "Agreed",
      body: agreementNote,
    });
  }
  // Framings before additions, as the design orders them, from one pass.
  const framings: Row[] = [];
  const additions: Row[] = [];
  for (const element of elements) {
    if (!isAwaitingAttention(element)) continue;
    if (element.source_tag === "ai_framing") {
      framings.push({
        key: element.element_id,
        kind: "framing",
        lead: "I read a framing",
        body: `“${element.text.replace(/^wherein /, "")}”. Not in your words; confirm or drop it above.`,
      });
    } else if (element.source_tag === "unverified") {
      additions.push({
        key: element.element_id,
        kind: "added",
        lead: "You added",
        body: `“${element.text}”. Not in the documents; flagged.`,
      });
    }
  }
  rows.push(...framings, ...additions);
  for (const question of questions) {
    const gap = question.completes;
    if (gap?.kind !== "gap_not_assumed" || !UNANSWERED.has(question.status))
      continue;
    rows.push({
      key: question.question_id,
      kind: "missing",
      lead: "Missing",
      body: `${gap.clause_prefix ?? "a value"}: the documents give no value for it. I flagged it and did not guess.`,
    });
  }
  return rows;
}

/**
 * "Where your description and mine differ": the attorney's intake account set
 * against the derived reading, row by row. With no account, the documents
 * alone are the other reading and the title says so.
 */
export function DifferenceCard({
  agreementNote,
  elements,
  questions,
}: {
  agreementNote: string | null;
  elements: readonly InventionElementResponse[];
  questions: readonly ServedQuestion[];
}) {
  const rows = differenceRows(agreementNote, elements, questions);
  // The heading follows the rows: an agreed row is there exactly when an account was.
  const hasAccount = rows[0]?.kind === "agree";
  return (
    <Panel
      as="section"
      aria-labelledby="difference-heading"
      className="mt-6 bg-secondary/40"
    >
      <MicroLabel as="h2" size="sm" id="difference-heading">
        {hasAccount
          ? "Where your description and mine differ"
          : "Where the documents and my reading differ"}
      </MicroLabel>
      <ul className="mt-2.5 space-y-1.5">
        {rows.map((row) => (
          <li
            key={`${row.kind}-${row.key}`}
            className="flex items-start gap-2.5 text-[12px] leading-relaxed"
          >
            <span
              aria-hidden
              className={cn(
                "mt-[2px] grid size-[18px] flex-none place-items-center rounded-md text-[11px] font-bold",
                MARK[row.kind].className,
              )}
            >
              {MARK[row.kind].glyph}
            </span>
            <span>
              <b>{row.lead}</b>. {row.body}
            </span>
          </li>
        ))}
      </ul>
      {rows.length === 0 && (
        <p className="mt-2 text-[12px] text-muted-foreground">
          Nothing differs. Every element is grounded or settled, and no gap is
          open.
        </p>
      )}
    </Panel>
  );
}
